import * as React from 'react'
import { JobNumberShape } from '../../api/api_data_types'
import { loadOrderNumberOptions } from '../../api/jobnumbers'
import { new_resources_record_path } from '../../routes'

type JobnumberSuggestionsProps = {
  term: string
  date: string
  enabled: boolean
}

export default function JobnumberSuggestions({ term, date, enabled }: JobnumberSuggestionsProps) {
  const [jobnumbers, setJobnumbers] = React.useState<JobNumberShape[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [hasLoaded, setHasLoaded] = React.useState(false)
  const [isVisible, setIsVisible] = React.useState(false)
  const requestIdRef = React.useRef(0)
  const hideTimerRef = React.useRef<number | null>(null)
  const normalizedTerm = term.trim()

  React.useEffect(() => {
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }

    if (!normalizedTerm) {
      requestIdRef.current += 1
      setJobnumbers([])
      setIsLoading(false)
      setHasLoaded(false)
      setIsVisible(false)
      return
    }

    if (!enabled) {
      hideTimerRef.current = window.setTimeout(() => {
        setIsVisible(false)
      }, 1000)

      return () => {
        if (hideTimerRef.current) {
          window.clearTimeout(hideTimerRef.current)
          hideTimerRef.current = null
        }
      }
    }

    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId
    setIsVisible(true)

    const timerId = window.setTimeout(() => {
      setIsLoading(true)
      setHasLoaded(false)

      loadOrderNumberOptions(normalizedTerm, (options) => {
        if (requestIdRef.current !== requestId) {
          return
        }

        setJobnumbers(options.slice(0, 3).map((option) => option.jobnumber))
        setIsLoading(false)
        setHasLoaded(true)
      })
    }, 250)

    return () => {
      window.clearTimeout(timerId)
    }
  }, [enabled, normalizedTerm])

  React.useEffect(() => {
    return () => {
      if (hideTimerRef.current) {
        window.clearTimeout(hideTimerRef.current)
      }
    }
  }, [])

  if (!normalizedTerm || !isVisible) {
    return null
  }

  return (
    <div className="mt-3">
      <div className="border rounded p-3 bg-light">
        <div>Ingen registreringer matcher filtret.</div>

        {isLoading && (
          <div className="mt-2">
            <i className="fa fa-spinner fa-spin mr-2" />
            Søger efter jobnumre...
          </div>
        )}

        {!isLoading && hasLoaded && jobnumbers.length > 0 && (
          <div className="mt-3">
            <div className="mb-2">Opret ny registrering for:</div>
            {jobnumbers.map((jobnumber) => (
              <a
                key={jobnumber.id}
                className="btn btn-outline-primary mr-2 mb-2"
                href={new_resources_record_path({ job_id: jobnumber.job_id, date })}
              >
                {jobnumber.job_id_title} - {jobnumber.customer_title}
              </a>
            ))}
          </div>
        )}

        {!isLoading && hasLoaded && jobnumbers.length === 0 && (
          <div className="mt-2 text-muted">Ingen jobnumre fundet.</div>
        )}
      </div>
    </div>
  )
}
